import PortalIdParser from 'PortalIdParser';
import CampaignRecord from '../data/CampaignRecord';
import { CAMPAIGN_PROPERTIES } from '../constants/crmCampaignProperties';
const CAMPAIGN_HUBSPOT_DEFINED_PROPERTIES = Object.values(CAMPAIGN_PROPERTIES);

/**
 *
 * CAMPAIGN RECORD => CRM OBJECT
 *
 * */
const crmObjectToRecordMap = {
  [CAMPAIGN_PROPERTIES.CREATED_AT]: 'createdAt',
  [CAMPAIGN_PROPERTIES.GOAL]: 'goal',
  [CAMPAIGN_PROPERTIES.GOAL_INTENTION]: 'goalIntention',
  [CAMPAIGN_PROPERTIES.GOAL_GROUP_CORRELATION_UUID]: 'goalGroupCorrelationUuid',
  [CAMPAIGN_PROPERTIES.CREATED_BY_USER_ID]: 'createdBy',
  [CAMPAIGN_PROPERTIES.NAME]: 'display_name',
  [CAMPAIGN_PROPERTIES.GUID]: 'guid',
  [CAMPAIGN_PROPERTIES.COLOR]: 'colorHex',
  [CAMPAIGN_PROPERTIES.BUDGET]: 'actualBudget',
  [CAMPAIGN_PROPERTIES.AUDIENCE]: 'audience',
  [CAMPAIGN_PROPERTIES.NOTES]: 'notes',
  [CAMPAIGN_PROPERTIES.END_DATE]: 'endedAt',
  [CAMPAIGN_PROPERTIES.OWNER]: 'owner',
  [CAMPAIGN_PROPERTIES.START_DATE]: 'startedAt',
  [CAMPAIGN_PROPERTIES.UTM]: 'utm',
  [CAMPAIGN_PROPERTIES.BUSINESS_UNIT_IDS]: 'businessUnitsInfo',
  [CAMPAIGN_PROPERTIES.CURRENCY_CODE]: 'currencyCode',
  [CAMPAIGN_PROPERTIES.BUDGET_AMOUNT]: 'numericBudget',
  [CAMPAIGN_PROPERTIES.SPEND_AMOUNT]: 'spendAmount',
  [CAMPAIGN_PROPERTIES.BUDGET_TOTAL_AMOUNT]: 'budgetTotalAmount',
  [CAMPAIGN_PROPERTIES.SPEND_TOTAL_AMOUNT]: 'spendTotalAmount',
  [CAMPAIGN_PROPERTIES.TEMPLATE_GUID]: 'templateGuid',
  [CAMPAIGN_PROPERTIES.SUPPORTS_ASSET_PLACEHOLDERS]: 'supportsAssetPlaceholders',
  [CAMPAIGN_PROPERTIES.STATUS]: 'status',
  [CAMPAIGN_PROPERTIES.ATTRIBUTION_ENABLED]: 'attributionEnabled',
  [CAMPAIGN_PROPERTIES.PROJECTED_BUDGET]: 'projectedBudget',
  [CAMPAIGN_PROPERTIES.REVENUE]: 'revenue',
  [CAMPAIGN_PROPERTIES.PROMPT_INPUTS]: 'promptInputs'
};
const campaignPropertyMappingConfig = {
  hs_action_metadata: {
    targetKey: 'actionMetadata',
    transformType: 'string'
  },
  hs_all_assigned_business_unit_ids: {
    targetKey: 'businessUnitsInfo',
    transformType: 'custom',
    customTransform: property => {
      const businessUnit = parseIntOrNull(property);
      return businessUnit != null ? {
        selectedBusinessUnits: [businessUnit]
      } : undefined;
    }
  },
  hs_attribution_enabled: {
    targetKey: 'attributionEnabled',
    transformType: 'boolean'
  },
  hs_audience: {
    targetKey: 'audience',
    transformType: 'string'
  },
  hs_ai_features: {
    targetKey: 'aiFeatures',
    transformType: 'json'
  },
  hs_budget_amount: {
    targetKey: 'numericBudget',
    transformType: 'int'
  },
  hs_budget_items_sum_amount: {
    targetKey: 'budgetTotalAmount',
    transformType: 'int'
  },
  hs_budget: {
    targetKey: 'actualBudget',
    transformType: 'string'
  },
  hs_brief_properties: {
    targetKey: 'briefProperties',
    transformType: 'json'
  },
  hs_brief_properties_v2: {
    targetKey: 'briefPropertiesV2',
    transformType: 'json'
  },
  hs_campaign_status: {
    targetKey: 'status',
    transformType: 'string'
  },
  hs_color_hex: {
    targetKey: 'colorHex',
    transformType: 'string'
  },
  hs_created_at: {
    targetKey: 'createdAt',
    transformType: 'int'
  },
  hs_created_by_user_id: {
    targetKey: 'createdBy',
    transformType: 'int'
  },
  hs_currency_code: {
    targetKey: 'currencyCode',
    transformType: 'string'
  },
  hs_end_date: {
    targetKey: 'endedAt',
    transformType: 'int'
  },
  hs_goal: {
    targetKey: 'goal',
    transformType: 'string'
  },
  hs_goal_intention: {
    targetKey: 'goalIntention',
    transformType: 'string'
  },
  hs_goal_group_correlation_uuid: {
    targetKey: 'goalGroupCorrelationUuid',
    transformType: 'string'
  },
  hs_influenced_closed_deal_goal: {
    targetKey: 'goals.influencedDeals',
    transformType: 'int'
  },
  hs_influenced_contact_goal: {
    targetKey: 'goals.influencedContacts',
    transformType: 'int'
  },
  hs_influenced_revenue: {
    targetKey: 'goals.influencedRevenue',
    transformType: 'int'
  },
  hs_new_contact_goal: {
    targetKey: 'goals.newContacts',
    transformType: 'int'
  },
  hs_session_goal: {
    targetKey: 'goals.sessions',
    transformType: 'int'
  },
  hs_lastmodifieddate: {
    targetKey: 'updatedAt',
    transformType: 'int'
  },
  hs_name: {
    targetKey: 'display_name',
    transformType: 'custom',
    customTransform: property => valueOrNull(property) || undefined
  },
  hs_notes: {
    targetKey: 'notes',
    transformType: 'string'
  },
  hs_origin_asset_id: {
    targetKey: 'guid',
    transformType: 'string'
  },
  hs_owner: {
    targetKey: 'owner',
    transformType: 'int'
  },
  hs_projected_budget: {
    targetKey: 'projectedBudget',
    transformType: 'string'
  },
  hs_revenue: {
    targetKey: 'revenue',
    transformType: 'int'
  },
  hs_spend_amount: {
    targetKey: 'spendAmount',
    transformType: 'int'
  },
  hs_spend_items_sum_amount: {
    targetKey: 'spendTotalAmount',
    transformType: 'int'
  },
  hs_start_date: {
    targetKey: 'startedAt',
    transformType: 'int'
  },
  hs_template_guid: {
    targetKey: 'templateGuid',
    transformType: 'string'
  },
  hs_utm: {
    targetKey: 'utm',
    transformType: 'custom',
    customTransform: property => valueOrNull(property) || undefined
  },
  hs_supports_asset_placeholders: {
    targetKey: 'supportsAssetPlaceholders',
    transformType: 'boolean'
  },
  hs_prompt_inputs: {
    targetKey: 'promptInputs',
    transformType: 'string'
  }
};

/**
 * Reusable function to map HubSpot campaign CRM properties to target object properties
 */
function mapHubSpotProperties(hsProperties) {
  const result = {};
  Object.entries(hsProperties).forEach(([hsKey, hsProperty]) => {
    const config = campaignPropertyMappingConfig[hsKey];
    if (!config) return;
    const {
      targetKey,
      transformType,
      customTransform
    } = config;
    let transformedValue;
    switch (transformType) {
      case 'string':
        transformedValue = valueOrNull(hsProperty);
        break;
      case 'int':
        transformedValue = parseIntOrNull(hsProperty);
        break;
      case 'boolean':
        transformedValue = valueOrNull(hsProperty) === 'true';
        break;
      case 'json':
        transformedValue = safeParseJSONOrNull(valueOrNull(hsProperty));
        break;
      case 'custom':
        transformedValue = customTransform ? customTransform(hsProperty) : null;
        break;
      default:
        transformedValue = valueOrNull(hsProperty);
    }

    // Handle nested property assignment (e.g., 'goals.influencedContacts')
    if (targetKey.includes('.')) {
      const [parentKey, childKey] = targetKey.split('.');
      if (!result[parentKey]) {
        result[parentKey] = {};
      }
      result[parentKey][childKey] = transformedValue;
    } else {
      result[targetKey] = transformedValue;
    }
  });
  return result;
}
function formatPropertyValue(value) {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }
  return value;
}
function getValidCustomPropertyValues(properties, options) {
  // Handle custom properties (non-hs_ prefixed properties)
  const isValidCustomProperty = options && options.isValidCustomProperty || (() => true);
  return Object.entries(properties).filter(([key]) => !CAMPAIGN_HUBSPOT_DEFINED_PROPERTIES.includes(key) && !key.startsWith('hs_') && isValidCustomProperty({
    name: key
  })).reduce((acc, [key, {
    value
  }]) => {
    acc[key] = value;
    return acc;
  }, {});
}
function toCrmObjectProperties(propertyMap) {
  return Object.keys(propertyMap).reduce((acc, key) => Object.assign({}, acc, {
    [key]: {
      value: formatPropertyValue(propertyMap[key])
    }
  }), {});
}

/**
 * Returns a crm object from a campaign record.
 *
 * @param {CampaignRecord} campaignRecord
 */
export function toCampaignCrmObject(campaignRecord) {
  const baseProperties = toCrmObjectProperties(Object.entries(crmObjectToRecordMap).reduce((acc, [crmObjectProperty, recordProperty]) => Object.assign({}, acc, {
    [crmObjectProperty]: campaignRecord[recordProperty]
  }), {}));
  const campaignGoals = campaignRecord.goals || {
    contacts: null,
    customers: null,
    influencedContacts: null,
    influencedDeals: null,
    influencedRevenue: null,
    newContacts: null,
    sessions: null,
    visits: null
  };
  const campaignGoalsProperties = toCrmObjectProperties({
    hs_influenced_contact_goal: campaignGoals.influencedContacts != null ? String(campaignGoals.influencedContacts) : null,
    hs_influenced_closed_deal_goal: campaignGoals.influencedDeals != null ? String(campaignGoals.influencedDeals) : null,
    hs_influenced_revenue: campaignGoals.influencedRevenue != null ? String(campaignGoals.influencedRevenue) : null,
    hs_new_contact_goal: campaignGoals.newContacts != null ? String(campaignGoals.newContacts) : null,
    hs_session_goal: campaignGoals.sessions != null ? String(campaignGoals.sessions) : null
  });
  const customProperties = toCrmObjectProperties(campaignRecord.customProperties || {});
  const campaignCrmObject = {
    objectId: campaignRecord.objectId,
    objectTypeId: '0-35',
    properties: Object.assign({}, baseProperties, customProperties, campaignGoalsProperties),
    portalId: Number(PortalIdParser.get())
  };
  return campaignCrmObject;
}

/**
 *
 * CRM OBJECT => CAMPAIGN RESPONSE / RECORD
 *
 * */

function valueOrNull(property) {
  return property ? property.value : null;
}
function parseIntOrNull(property) {
  const value = valueOrNull(property);
  if (value) {
    const parsedValue = parseInt(value, 10);
    return isNaN(parsedValue) ? null : parsedValue;
  }
  return null;
}
function safeParseJSONOrNull(json) {
  if (!json) {
    return null;
  }
  try {
    return JSON.parse(json);
  } catch (_unused) {
    return null;
  }
}
/**
 * Returns campaign response properties from crm object properties.
 */
export function fromCrmPropertiesToCampaignResponseProperties({
  properties
}, options) {
  // Use the reusable mapping function for all hs_ properties
  const mappedProperties = mapHubSpotProperties(Object.assign({}, Object.keys(campaignPropertyMappingConfig).reduce((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {}), properties));
  const customProperties = getValidCustomPropertyValues(properties, options);

  // Ensure goals object has all required properties with null defaults
  const goals = Object.assign({
    contacts: null,
    customers: null,
    visits: null,
    influencedContacts: null,
    influencedDeals: null,
    influencedRevenue: null,
    newContacts: null,
    sessions: null
  }, mappedProperties.goals);
  return Object.assign({}, mappedProperties, {
    customProperties,
    goals,
    deleted: false,
    steps: null
  });
}
export const fromCrmPropertiesToPartialCampaignProperties = ({
  properties,
  options
}) => {
  const mappedProperties = mapHubSpotProperties(properties);
  const customProperties = getValidCustomPropertyValues(properties, options);
  const result = Object.assign({}, mappedProperties);
  if (Object.keys(customProperties).length > 0) {
    result.customProperties = customProperties;
  }
  return result;
};

/**
 * Returns a campaign response from a crm object.
 */
export function toCampaignResponse({
  properties,
  objectId,
  portalId
}, options) {
  const campaignResponseProperties = fromCrmPropertiesToCampaignResponseProperties({
    properties
  }, options);
  return Object.assign({}, campaignResponseProperties, {
    objectId,
    portalId
  });
}

/**
 * Returns a campaign record from a crm object
 */
export function toLegacyCampaignRecord(crmObject) {
  return CampaignRecord.from(toCampaignResponse(crmObject));
}